import { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
import { AppView } from "../AppView";
import { DragRotateBehaviour, MeshUtils, Rectangle3D } from "@dreamcam/babylon-tools";
import { ButtonHandler, PressEvent, PressType } from "../ButtonHandler";
import { ControllerButton, XRInputHelper } from "../XRInputHelper";
import { Env } from "../../settings/Env";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture.js";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { LoginDialog } from "./LoginDialog";
import { InfoDialog } from "./InfoDialog";
import { Keyboard3D } from "./keyboard/Keyboard3D";
import { HideKeyboardComponent } from "./HideKeyboardComponent";
import { MeshName } from "../MeshName";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { MessengerPanel } from "./MessengerPanel";
import { MessengerChatListPanel } from "./MessengerChatListPanel";
import { SubtitlePanel } from "./subtitles/SubtitlePanel";
export class BaseRoomMenu {
    // noinspection JSUnusedGlobalSymbols
    get loginDialog() { return this._loginDialog; }
    get infoDialog() { return this._infoDialog; }
    constructor(callbacks, messengerCallbacks) {
        this._subtitlesRootNode = null;
        this._subtitlesDragBehaviour = null;
        this._subtitlesPanelMesh = null;
        this._subtitlesLanguageListMesh = null;
        this._subtitlesDragHandleMesh = null;
        this._isSubtitlesDragging = false;
        this._menuPanelMeshes = [];
        this._meshMaterialsDictionary = [];
        this._subtitlesDragMaterialsDictionary = [];
        this._isDragging = false;
        this._isSimulatingMove = false;
        this._simulatedPickedInfo = null;
        this._isSimulatingSubtitlesMove = false;
        this._createPanelMesh = (name, width, height, position, parentNode = undefined, renderingGroupId = DcRenderingGroup.dialogs, includeInDragDictionary = true, addToMenuMeshesList = true) => {
            if (!parentNode)
                parentNode = this._menuRootNode;
            const result = MeshUtils.createPlaneMesh(name, width, height, AppView.getInstance().scene, parentNode, renderingGroupId);
            result.mesh.position = position;
            if (addToMenuMeshesList)
                this._menuPanelMeshes.push(result);
            if (includeInDragDictionary)
                this.addToDragDictionary(result);
            const extraMeshPointerAddon = XRInputHelper.addPointerAddon(result.mesh);
            extraMeshPointerAddon.onPointerOut.add(() => result.notifyChildrenOnPointerOut());
            return result;
        };
        this._callbacks = callbacks;
        this._messengerCallbacks = messengerCallbacks;
        const appView = AppView.getInstance();
        this._menuRootNode = new TransformNode("menuRootNode", appView.scene);
        this._menuRootNode.parent = appView.guiRootNode;
        this._buttonHandler = new ButtonHandler();
        this._subtitlesButtonHandler = new ButtonHandler();
        this._dragAndDropMesh = this._createDragAndDropMesh();
        // this._dragAndDropMesh.texture.background = "blue";
        const dragAndDropControl = new Rectangle3D("dragAndDropControl");
        dragAndDropControl.thickness = 0;
        dragAndDropControl.onPointerEnterObservable.add(() => { dragAndDropControl.stateController.changeState("hover"); });
        dragAndDropControl.onPointerOutObservable.add(() => { dragAndDropControl.stateController.changeState("normal"); });
        dragAndDropControl.onPointerDownObservable.add(() => { dragAndDropControl.stateController.changeState("pressed"); });
        dragAndDropControl.onPointerUpObservable.add(() => { dragAndDropControl.stateController.changeState("hover"); });
        this._dragAndDropMesh.texture.addControl(dragAndDropControl);
        const dragAndDropIndicator = new Rectangle3D("dragAndDropIndicator");
        dragAndDropIndicator.heightInPixels = this._dragAndDropMesh.m2px(Env.broadcast.menu.dragAndDrop.indicatorHeight);
        dragAndDropIndicator.widthInPixels = this._dragAndDropMesh.m2px(Env.broadcast.menu.dragAndDrop.indicatorWidth);
        dragAndDropIndicator.cornerRadius = this._dragAndDropMesh.m2px(Env.broadcast.menu.dragAndDrop.cornerRadius);
        dragAndDropIndicator.stateController.setStates(Env.broadcast.menu.dragAndDrop.states);
        dragAndDropControl.addControl(dragAndDropIndicator);
        this._dragBehaviour = new DragRotateBehaviour();
        this._dragBehaviour.attach(this._menuRootNode);
        this._dragBehaviour.draggableMeshes = [this._dragAndDropMesh.mesh];
        this._dragBehaviour.onDragStartObservable.add(() => this._onDragStarted());
        this._dragBehaviour.onDragEndObservable.add(() => this._onDragEnded());
        if (appView.loginApiAuthAsync && appView.signupApiAuthAsync) {
            this._loginDialog = new LoginDialog(this._menuRootNode, {
                signInCallback: async (username, password) => {
                    return appView.loginApiAuthAsync(username, password);
                },
                signupCallback: async (email, username, password) => {
                    return appView.signupApiAuthAsync(email, username, password);
                },
                closeCallback: () => {
                    this._onCloseLoginDialog();
                },
                onShowCallback: () => {
                    this._onShowLoginDialog();
                },
                onHideCallback: () => {
                    this._onHideLoginDialog();
                },
            });
            this._loginDialog.hide();
            this.addToDragDictionary(this._loginDialog.mesh2D);
        }
        this._infoDialog = new InfoDialog(this._menuRootNode);
        this._infoDialog.hide();
        this.addToDragDictionary(this._infoDialog.mesh2d);
        appView.scene.onPointerObservable.add((e) => {
            if (this._isSimulatingMove && e.pickInfo !== null && e.pickInfo.pickedMesh !== null && e.pickInfo.pickedMesh.name !== this._dragAndDropMesh.mesh.name && e.pickInfo !== this._simulatedPickedInfo) {
                e.pickInfo.pickedMesh = this._dragAndDropMesh.mesh;
                this._simulatedPickedInfo = e.pickInfo;
                appView.scene.simulatePointerMove(e.pickInfo);
            }
            else if (this._isSimulatingSubtitlesMove && this._subtitlesDragHandleMesh && e.pickInfo !== null && e.pickInfo.pickedMesh !== null && e.pickInfo.pickedMesh.name !== this._subtitlesDragHandleMesh.mesh.name && e.pickInfo !== this._simulatedPickedInfo) {
                e.pickInfo.pickedMesh = this._subtitlesDragHandleMesh.mesh;
                this._simulatedPickedInfo = e.pickInfo;
                appView.scene.simulatePointerMove(e.pickInfo);
            }
        });
        const menu = Env.broadcast.menu;
        this._keyboard = new Keyboard3D(this._getKeyboardMeshName(), appView.scene, this._menuRootNode);
        this._keyboard.mesh.position = this._getKeyboardPosition();
        this._keyboard.mesh.rotateAround(this._keyboard.mesh.position.add(Vector3.Up().scale(this._keyboard.height / 2)), Vector3.Right(), menu.keyboard.angleInDegrees * Math.PI / 180);
        this._keyboard.control.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        this._hideKeyboardComponent = new HideKeyboardComponent(this._getHideKeyboardMeshName(), this._keyboard);
    }
    setDragBehaviourActivity(isActive) {
        if (isActive) {
            this._dragBehaviour.attach(this._menuRootNode);
        }
        else {
            this._dragBehaviour.detach();
        }
    }
    _onCloseLoginDialog() { }
    _onShowLoginDialog() { }
    _onHideLoginDialog() { }
    _getMeshPanelPosition(menuCenter, verticalOffset) {
        return new Vector3(menuCenter.x, menuCenter.y + verticalOffset, menuCenter.z);
    }
    _showPanels() {
        this._menuPanelMeshes.forEach((mesh) => MeshUtils.show(mesh));
    }
    _hidePanels() {
        this._menuPanelMeshes.forEach((mesh) => MeshUtils.hide(mesh));
    }
    _onSubtitlesDragStarted() {
        this._isSubtitlesDragging = true;
        this._subtitlesDragMaterialsDictionary.forEach((v) => {
            v.mesh.mesh.material = v.dragMaterial;
            v.mesh.mesh.isPickable = false;
        });
    }
    _onSubtitlesDragEnded() {
        this._isSubtitlesDragging = false;
        this._subtitlesDragMaterialsDictionary.forEach((v) => {
            v.mesh.mesh.material = v.actualMaterial;
            v.mesh.mesh.isPickable = true;
        });
    }
    _cancelDragState() {
        this._dragBehaviour.reset();
        if (this._isDragging) {
            this._onDragEnded();
        }
        if (this._subtitlesDragBehaviour) {
            this._subtitlesDragBehaviour.reset();
            if (this._isSubtitlesDragging) {
                this._onSubtitlesDragEnded();
            }
        }
        this._isSimulatingMove = false;
        this._isSimulatingSubtitlesMove = false;
        this._simulatedPickedInfo = null;
    }
    _onDragStarted() {
        this._isDragging = true;
        this._meshMaterialsDictionary.forEach((v) => {
            v.mesh.mesh.material = v.dragMaterial;
            v.mesh.mesh.isPickable = false;
        });
    }
    _onDragEnded() {
        this._isDragging = false;
        this._meshMaterialsDictionary.forEach((v) => {
            v.mesh.mesh.material = v.actualMaterial;
            v.mesh.mesh.isPickable = true;
        });
    }
    addToDragDictionary(mesh) {
        const dragMaterial = new StandardMaterial("dragMaterial", this._menuRootNode.getScene());
        dragMaterial.useAlphaFromDiffuseTexture = true;
        const dragTexture = new AdvancedDynamicTexture("dragTexture", mesh.m2px(mesh.width), mesh.m2px(mesh.height), this._menuRootNode.getScene());
        dragMaterial.diffuseTexture = dragTexture;
        const dragBackground = new Rectangle3D("dragBackground");
        dragBackground.thickness = 0;
        dragBackground.background = Env.broadcast.menu.dragAndDrop.dragBackground;
        dragBackground.cornerRadius = mesh.m2px(Env.broadcast.menu.dragAndDrop.dragCornerRadius);
        dragTexture.addControl(dragBackground);
        this._meshMaterialsDictionary.push({ mesh: mesh, actualMaterial: mesh.mesh.material, dragMaterial });
    }
    _hideInternal() {
        this._cancelDragState();
        this._buttonHandler.removeHandlers();
        this._hidePanels();
    }
    _showInternal() {
        this._dragBehaviour.reset();
        this._isDragging = false;
        this._meshMaterialsDictionary.forEach((v) => {
            v.mesh.mesh.material = v.actualMaterial;
            v.mesh.mesh.isPickable = true;
        });
        this._buttonHandler.addHandlers({
            button: ControllerButton.Squeeze, eventType: PressType.Hold, callback: (event, pick, controllerId) => {
                if (!this._getMenuActivity())
                    return;
                if (event === PressEvent.Press) {
                    if (pick !== null && pick.pickedMesh?.isDescendantOf(this._menuRootNode)) {
                        pick.pickedMesh = this._dragAndDropMesh.mesh;
                        AppView.getInstance().scene.simulatePointerDown(pick, { pointerId: controllerId, pointerType: "xr" });
                        this._isSimulatingMove = true;
                    }
                }
                else {
                    if (pick !== null) {
                        pick.pickedMesh = this._dragAndDropMesh.mesh;
                        AppView.getInstance().scene.simulatePointerUp(pick, { pointerId: controllerId, pointerType: "xr" });
                        this._isSimulatingMove = false;
                    }
                }
            }
        });
        this._showPanels();
    }
    _createSubtitlesUi(subtitleController) {
        const appView = AppView.getInstance();
        const menu = Env.broadcast.menu;
        const rootNode = new TransformNode("subtitlesRootNode", appView.scene);
        rootNode.parent = appView.guiRootNode;
        rootNode.position = new Vector3(menu.position.center.x, menu.position.center.y + menu.subtitles.verticalOffset, menu.subtitles.distance);
        rootNode.rotationQuaternion = Quaternion.FromEulerAngles(menu.subtitles.pitchInDegrees * Math.PI / 180, 0, 0);
        const panelMesh = MeshUtils.createPlaneMesh(MeshName.broadcastSubtitlesPanel, menu.subtitles.width, menu.subtitles.height, appView.scene, rootNode, DcRenderingGroup.aboveDialogs);
        const languageListMesh = MeshUtils.createPlaneMesh(MeshName.broadcastSubtitlesLanguageList, menu.subtitles.languageList.width, menu.subtitles.languageList.height, appView.scene, rootNode, DcRenderingGroup.aboveDialogs);
        languageListMesh.mesh.position = new Vector3((menu.subtitles.width / 2) + menu.subtitles.languageList.offset.left + (menu.subtitles.languageList.width / 2), menu.subtitles.languageList.offset.top, 0);
        MeshUtils.setVisibility(languageListMesh, false);
        const dragHandleMesh = this._createPanelMesh(MeshName.broadcastSubtitlesDragHandle, menu.subtitles.handle.width, menu.subtitles.handle.height, new Vector3(0, -(menu.subtitles.height / 2)
            - (menu.subtitles.handle.height / 2), 0), rootNode, DcRenderingGroup.aboveDialogs, false, false);
        dragHandleMesh.texture.background = "transparent";
        const dragHandleControl = new Rectangle3D("subtitlesDragHandleControl");
        dragHandleControl.thickness = 0;
        dragHandleMesh.texture.addControl(dragHandleControl);
        const dragHandleIndicator = new Rectangle3D("subtitlesDragHandleIndicator");
        dragHandleIndicator.heightInPixels = dragHandleMesh.m2px(menu.dragAndDrop.indicatorHeight);
        dragHandleIndicator.widthInPixels = dragHandleMesh.m2px(menu.dragAndDrop.indicatorWidth);
        dragHandleIndicator.cornerRadius = dragHandleMesh.m2px(menu.dragAndDrop.cornerRadius);
        dragHandleIndicator.stateController.setStates(menu.dragAndDrop.states);
        dragHandleControl.addControl(dragHandleIndicator);
        dragHandleIndicator.stateController.changeState("normal");
        dragHandleControl.onPointerEnterObservable.add(() => { dragHandleIndicator.stateController.changeState("hover"); });
        dragHandleControl.onPointerOutObservable.add(() => { dragHandleIndicator.stateController.changeState("normal"); });
        dragHandleControl.onPointerDownObservable.add(() => { dragHandleIndicator.stateController.changeState("pressed"); });
        dragHandleControl.onPointerUpObservable.add(() => { dragHandleIndicator.stateController.changeState("hover"); });
        panelMesh.mesh.isPickable = true;
        dragHandleMesh.mesh.isPickable = true;
        MeshUtils.setVisibility(dragHandleMesh, false);
        new SubtitlePanel(panelMesh, subtitleController, languageListMesh);
        const createSubtitlesPanelDragMaterial = (mesh) => {
            const dragMaterial = new StandardMaterial("subtitlesDragMaterial", this._menuRootNode.getScene());
            dragMaterial.useAlphaFromDiffuseTexture = true;
            const dragTexture = new AdvancedDynamicTexture("subtitlesDragTexture", mesh.m2px(mesh.width), mesh.m2px(mesh.height), this._menuRootNode.getScene());
            dragMaterial.diffuseTexture = dragTexture;
            const dragBackground = new Rectangle3D("subtitlesDragBackground");
            dragBackground.thickness = 0;
            dragBackground.background = Env.broadcast.menu.dragAndDrop.dragBackground;
            dragBackground.widthInPixels = mesh.m2px(mesh.width);
            dragBackground.heightInPixels = mesh.m2px(mesh.height);
            dragBackground.cornerRadius = mesh.m2px(Env.broadcast.menu.cornerRadius);
            dragBackground.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
            dragBackground.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
            dragTexture.addControl(dragBackground);
            return dragMaterial;
        };
        const createSubtitlesLanguageListDragMaterial = (mesh) => {
            const cfg = menu.subtitles.languageList;
            const dragMaterial = new StandardMaterial("subtitlesLangListDragMaterial", this._menuRootNode.getScene());
            dragMaterial.useAlphaFromDiffuseTexture = true;
            const dragTexture = new AdvancedDynamicTexture("subtitlesLangListDragTexture", mesh.m2px(mesh.width), mesh.m2px(mesh.height), this._menuRootNode.getScene());
            dragMaterial.diffuseTexture = dragTexture;
            const dragBackground = new Rectangle3D("subtitlesLangListDragBackground");
            dragBackground.thickness = 0;
            dragBackground.background = Env.broadcast.menu.dragAndDrop.dragBackground;
            dragBackground.widthInPixels = mesh.m2px(cfg.width);
            dragBackground.heightInPixels = mesh.m2px(cfg.height);
            dragBackground.cornerRadius = mesh.m2px(cfg.cornerRadius);
            dragBackground.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
            dragBackground.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
            dragTexture.addControl(dragBackground);
            return dragMaterial;
        };
        const subtitlesDragDictionary = [
            { mesh: panelMesh, actualMaterial: panelMesh.mesh.material, dragMaterial: createSubtitlesPanelDragMaterial(panelMesh) },
            { mesh: languageListMesh, actualMaterial: languageListMesh.mesh.material, dragMaterial: createSubtitlesLanguageListDragMaterial(languageListMesh) },
        ];
        this._subtitlesDragMaterialsDictionary = subtitlesDragDictionary;
        const dragBehaviour = new DragRotateBehaviour();
        dragBehaviour.attach(rootNode);
        dragBehaviour.draggableMeshes = [dragHandleMesh.mesh];
        dragBehaviour.onDragStartObservable.add(() => this._onSubtitlesDragStarted());
        dragBehaviour.onDragEndObservable.add(() => this._onSubtitlesDragEnded());
        this._subtitlesButtonHandler.addHandlers({
            button: ControllerButton.Squeeze, eventType: PressType.Hold, callback: (event, pick, controllerId) => {
                if (!this._subtitlesRootNode || !this._subtitlesDragHandleMesh)
                    return;
                if (event === PressEvent.Press) {
                    if (pick !== null && pick.pickedMesh?.isDescendantOf(this._subtitlesRootNode)) {
                        pick.pickedMesh = this._subtitlesDragHandleMesh.mesh;
                        AppView.getInstance().scene.simulatePointerDown(pick, { pointerId: controllerId, pointerType: "xr" });
                        this._isSimulatingSubtitlesMove = true;
                    }
                }
                else {
                    if (pick !== null) {
                        pick.pickedMesh = this._subtitlesDragHandleMesh.mesh;
                        AppView.getInstance().scene.simulatePointerUp(pick, { pointerId: controllerId, pointerType: "xr" });
                        this._isSimulatingSubtitlesMove = false;
                    }
                }
            }
        });
        return { rootNode, panelMesh, languageListMesh, dragHandleMesh, dragBehaviour };
    }
    _createMessengerPanel(callbacks) {
        const result = MessengerPanel.createMessengerPanel({
            messengerPanelMeshName: this._getMessengerPanelMeshName(),
            messengerPanelPosition: this._getMessengerPanelPosition(),
            ellipsisSubmenuMeshName: this._getMessengerEllipsisSubmenuMeshName(),
            linkWarningDialogMeshName: this._getMessengerLinkWarningDialogMeshName(),
            parentNode: this._menuRootNode,
            keyboard: this._keyboard,
            hideKeyboardComponent: this._hideKeyboardComponent,
            tipsKeyboardMeshName: this._getMessengerTipsKeyboardMeshName(),
            callbacks,
        });
        this.addToDragDictionary(result.mesh2d);
        this.addToDragDictionary(result.linkWarningDialog.mesh2d);
        this.addToDragDictionary(result.tipsKeyboardMesh2d);
        return result;
    }
    _createMessengerChatListPanel() {
        const result = MessengerChatListPanel.createMessengerChatListPanel({
            messengerChatListMeshName: this._getMessengerChatListPanelMeshName(),
            messengerChatListPosition: this._getMessengerChatListPanelPosition(),
            parentNode: this._menuRootNode,
        });
        this.addToDragDictionary(result.mesh2d);
        return result;
    }
    showLoginDialog(tab) {
        if (this._loginDialog) {
            this._loginDialog.show(tab);
        }
        else {
            (async () => {
                const appView = AppView.getInstance();
                if (tab === "signUp" && appView.signupAsync) {
                    await appView.signupAsync();
                }
                else if (tab === "signIn" && appView.loginAsync) {
                    await appView.loginAsync();
                }
            })();
        }
    }
}
